import { App, Input, Modal, Skeleton } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  assistantApi,
  useAssistant,
  useDeleteAssistantMutation,
  usePatchAssistantMutation,
  type Assistant,
} from '@entities/assistant';
import { ModelAvatar, useModels } from '@entities/llm';
import { kbApi } from '@entities/knowledge';
import { extractErrorMessage } from '@shared/api';
import { IconCheck, TopBar, rub } from '@shared/ui/v2';
import { KbSuggestionCard } from './KbSuggestionCard';
import { useUnsavedGuard } from '@shared/ui/v2';
import './settings.css';

/** «Стиль ответов» (Р41): три варианта вместо числа температуры. */
const STYLES = [
  { id: 'strict', label: 'Строго', hint: 'Только по документам', t: 0.1 },
  { id: 'balanced', label: 'Сбалансированно', hint: 'Для поддержки', t: 0.3 },
  { id: 'lively', label: 'Живее', hint: 'Для продаж', t: 0.8 },
] as const;

function styleOf(t: number | null | undefined): (typeof STYLES)[number] {
  const v = Number(t ?? 0.3);
  if (v <= 0.2) return STYLES[0];
  if (v >= 0.6) return STYLES[2];
  return STYLES[1];
}

interface Form {
  prompt: string;
  name: string;
  show_images: boolean;
  temperature: number;
}

function formOf(a: Assistant): Form {
  // Температуру не округляем до варианта стиля: меняется, только если клиент выбрал стиль.
  return {
    prompt: a.prompt ?? '',
    name: a.name,
    show_images: a.show_images,
    temperature: Number(a.temperature ?? 0.3),
  };
}

function ModelCard({ assistant }: { assistant: Assistant }) {
  const { data: models } = useModels();
  const m = models?.find((x) => x.id === assistant.model_id);
  const to = `/assistants/${assistant.id}/settings/model`;
  if (!models) return <Skeleton active paragraph={{ rows: 2 }} />;
  if (!m) {
    return (
      <div className="st-model-head">
        <div style={{ flex: 1 }}>
          <h2 className="st-cap">Модель</h2>
          <div style={{ fontWeight: 700 }}>Модель не выбрана</div>
        </div>
        <Link to={to} className="v2-link">
          Выбрать
        </Link>
      </div>
    );
  }
  const price = m.price_per_answer_rub ?? m.price_per_answer_estimate_rub;
  return (
    <>
      <div className="st-model-head">
        <ModelAvatar model={m} size={38} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="st-cap">Модель</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{m.title}</span>
            {m.verified && <span className="st-rec">Рекомендовано RiChat</span>}
          </div>
        </div>
        <Link to={to} className="v2-link">
          Сменить
        </Link>
      </div>
      <div className="st-metrics">
        {m.eval_score != null && (
          <span>
            Верных ответов <b>{Math.round(m.eval_score)}%</b>
          </span>
        )}
        {m.eval_honesty != null && (
          <span>
            Не выдумывает <b>{Math.round(m.eval_honesty)}%</b>
          </span>
        )}
        {m.eval_seconds != null && (
          <span>
            Ответ{' '}
            <b>
              ~{Math.round(m.eval_seconds)}
              {'\u00a0'}с
            </b>
          </span>
        )}
        {price != null && (
          <span>
            Цена ответа <b>~{rub(price)}</b>
          </span>
        )}
      </div>
      <div className="v2-xs v2-muted" style={{ marginTop: 6 }}>
        {m.vendor_title} · данные обрабатываются {m.data_in_russia ? 'в России' : 'за рубежом'}
      </div>
    </>
  );
}

/** Экран 3 «Ассистент → Настройка» (утверждён 28.10.2026). */
export function AssistantSettingsPage() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: assistant, isLoading, error } = useAssistant(id);
  const patch = usePatchAssistantMutation(id ?? '');
  const del = useDeleteAssistantMutation();
  const [form, setForm] = useState<Form | null>(null);
  const [improving, setImproving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delName, setDelName] = useState('');
  const [saved, setSaved] = useState(false);
  const closeSaved = useCallback(() => setSaved(false), []);

  useEffect(() => {
    if (assistant) setForm(formOf(assistant));
  }, [assistant]);

  const initial = useMemo(() => (assistant ? formOf(assistant) : null), [assistant]);
  const dirty = !!form && !!initial && JSON.stringify(form) !== JSON.stringify(initial);
  useUnsavedGuard(dirty);

  const addedSugg = useRef<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const save = async () => {
    if (!form || !dirty) return;
    if (!form.name.trim()) {
      message.error('Название не может быть пустым');
      return;
    }
    try {
      await patch.mutateAsync({
        name: form.name.trim(),
        prompt: form.prompt,
        show_images: form.show_images,
        temperature: form.temperature,
      });
      if (addedSugg.current) {
        kbApi.dismissSuggestion(id!, addedSugg.current).catch(() => undefined);
        addedSugg.current = null;
      }
      setSaved(true);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const improve = async () => {
    if (!form || !id) return;
    if (form.prompt.trim().length < 3) {
      message.info('Напишите хотя бы пару фраз — ИИ их улучшит');
      return;
    }
    setImproving(true);
    try {
      const text = await assistantApi.improveInstruction(id, form.prompt);
      modal.confirm({
        title: 'ИИ предлагает новую редакцию инструкции',
        width: 640,
        icon: null,
        content: <div className="st-proposal">{text}</div>,
        okText: 'Заменить инструкцию',
        cancelText: 'Оставить как было',
        onOk: () => set('prompt', text),
      });
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setImproving(false);
    }
  };

  const doDelete = async () => {
    if (!assistant) return;
    try {
      await del.mutateAsync(assistant.id);
      message.success('Ассистент удалён');
      navigate('/', { replace: true });
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <>
      <TopBar
        section
        title="Настройка"
        hint="Изменения применятся сразу во всех каналах"
        actions={
          <button
            type="button"
            className="v2-btn is-primary"
            disabled={!dirty || patch.isPending}
            onClick={save}
          >
            {patch.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        }
      />
      <div className="v2-content is-section">
        {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {error && (
          <div className="v2-card v2-card-pad">
            Не удалось открыть ассистента: {extractErrorMessage(error)}
          </div>
        )}
        {assistant && form && (
          <div className="st-grid">
            <div className="st-col">
              <section aria-labelledby="h-prompt" className="v2-card st-card">
                <div className="st-row">
                  <h2 id="h-prompt" className="v2-h2">
                    Инструкция
                  </h2>
                  <button
                    type="button"
                    className="st-ai-btn"
                    onClick={improve}
                    disabled={improving}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8zM19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8z" />
                    </svg>
                    {improving ? 'ИИ думает…' : 'Улучшить с помощью ИИ'}
                  </button>
                </div>
                <label htmlFor="prompt" className="v2-small v2-muted">
                  Как ассистент общается и что ему можно. Факты о товарах и ценах — в базу знаний.
                </label>
                <textarea
                  id="prompt"
                  className="st-textarea"
                  rows={12}
                  value={form.prompt}
                  onChange={(e) => set('prompt', e.target.value)}
                />
              </section>
              <KbSuggestionCard
                assistantId={assistant.id}
                onAdd={(items, sig) => {
                  addedSugg.current = sig;
                  set(
                    'prompt',
                    `${form.prompt.trim()}\n${items.map((i) => `- ${i}`).join('\n')}`.trim(),
                  );
                }}
              />
            </div>

            <div className="st-col">
              <section
                aria-labelledby="h-model"
                className="v2-card st-card"
                style={{ display: 'block' }}
              >
                <ModelCard assistant={assistant} />
              </section>

              <section aria-labelledby="h-style" className="v2-card st-card">
                <h2 id="h-style" className="v2-h2">
                  Стиль ответов
                </h2>
                <div role="radiogroup" aria-label="Стиль ответов" className="st-styles">
                  {STYLES.map((s) => {
                    const on = styleOf(form.temperature).id === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        className={on ? 'st-style is-on' : 'st-style'}
                        onClick={() => set('temperature', s.t)}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div>
                        <div className="v2-xs v2-muted">{s.hint}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="st-switch-row">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Картинки в ответах</div>
                    <div className="v2-xs v2-muted">Схемы и фото из документов</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.show_images}
                    aria-label="Картинки в ответах"
                    className={form.show_images ? 'st-switch is-on' : 'st-switch'}
                    onClick={() => set('show_images', !form.show_images)}
                  >
                    <span />
                  </button>
                </div>
              </section>

              <section aria-labelledby="h-name" className="v2-card st-card">
                <h2 id="h-name" className="v2-h2">
                  Название
                </h2>
                <label htmlFor="nm" className="v2-sr">
                  Название ассистента
                </label>
                <input
                  id="nm"
                  type="text"
                  className="st-input"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                />
                <button
                  type="button"
                  className="st-delete"
                  onClick={() => {
                    setDelName('');
                    setDelOpen(true);
                  }}
                >
                  Удалить ассистента
                </button>
              </section>
            </div>
          </div>
        )}
      </div>

      {saved && id && <SavedToast aid={id} onClose={closeSaved} />}

      <Modal
        open={delOpen}
        title="Удалить ассистента?"
        okText="Удалить навсегда"
        cancelText="Отмена"
        okButtonProps={{
          danger: true,
          disabled: delName.trim() !== assistant?.name.trim(),
          loading: del.isPending,
        }}
        onOk={doDelete}
        onCancel={() => setDelOpen(false)}
      >
        <p>
          Удалятся база знаний, каналы, диалоги и рассылки ассистента. Отменить нельзя. Баланс
          аккаунта останется.
        </p>
        <p style={{ marginBottom: 6 }}>
          Чтобы подтвердить, введите название: <b>{assistant?.name}</b>
        </p>
        <Input value={delName} onChange={(e) => setDelName(e.target.value)} autoFocus />
      </Modal>
    </>
  );
}

/**
 * После «Сохранить» — подсказка, что делать дальше: проверить ответы в тест-чате
 * или подключить канал. Само закрывается через 15 секунд (если не навели мышь).
 */
function SavedToast({ aid, onClose }: { aid: string; onClose: () => void }) {
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (hover) return;
    const t = window.setTimeout(onClose, 15000);
    return () => window.clearTimeout(t);
  }, [hover, onClose]);
  return (
    <div
      role="status"
      className="kbw-toast st-saved"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span className="kbw-ok">
          <IconCheck size={12} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>Настройки сохранены</div>
          <div className="v2-xs v2-muted">
            Уже действуют во всех каналах. Проверьте, как ассистент отвечает теперь.
          </div>
        </div>
        <button
          type="button"
          className="v2-icon-btn"
          style={{ width: 26, height: 26 }}
          onClick={onClose}
          aria-label="Закрыть"
        >
          ×
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <Link to={`/assistants/${aid}/channels`} className="v2-btn" onClick={onClose}>
          Каналы
        </Link>
        <Link to={`/assistants/${aid}/test-chat`} className="v2-btn is-primary" onClick={onClose}>
          Проверить в тест-чате
        </Link>
      </div>
    </div>
  );
}
